import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { NotificationResponse } from "src/app/shared/models/notification-response.model";

export type NotificationConnectionStatus = "disconnected" | "connecting" | "connected" | "polling";

@Injectable({ providedIn: "root" })
export class NotificationStoreService {
  private readonly notificationsSubject = new BehaviorSubject<NotificationResponse[]>([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private readonly connectionStatusSubject = new BehaviorSubject<NotificationConnectionStatus>("disconnected");

  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly unreadCount$ = this.unreadCountSubject.asObservable();
  readonly connectionStatus$ = this.connectionStatusSubject.asObservable();

  get notifications(): NotificationResponse[] {
    return this.notificationsSubject.value;
  }

  get unreadCount(): number {
    return this.unreadCountSubject.value;
  }

  setNotifications(notifications: NotificationResponse[]): void {
    this.notificationsSubject.next(this.dedupeById(notifications).slice(0, 20));
  }

  prependNotification(notification: NotificationResponse): boolean {
    const existed = this.notifications.some((item) => item.id === notification.id);
    this.setNotifications([notification, ...this.notifications]);
    return !existed;
  }

  setUnreadCount(count: number): void {
    this.unreadCountSubject.next(Math.max(Number(count) || 0, 0));
  }

  markRead(notification: NotificationResponse): void {
    this.setNotifications(
      this.notifications.map((item) => item.id === notification.id ? notification : item)
    );
  }

  markAllRead(): void {
    this.setNotifications(this.notifications.map((item) => ({ ...item, read: true })));
    this.setUnreadCount(0);
  }

  removeNotification(id: number): void {
    this.setNotifications(this.notifications.filter((item) => item.id !== id));
  }

  setConnectionStatus(status: NotificationConnectionStatus): void {
    this.connectionStatusSubject.next(status);
  }

  clear(): void {
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
    this.connectionStatusSubject.next("disconnected");
  }

  private dedupeById(notifications: NotificationResponse[]): NotificationResponse[] {
    const seen = new Set<number>();
    return notifications.filter((notification) => {
      if (seen.has(notification.id)) {
        return false;
      }

      seen.add(notification.id);
      return true;
    });
  }
}
